import { Component, DestroyRef, OnInit, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { skip, firstValueFrom } from "rxjs";
import { TranslocoService, provideTranslocoScope } from "@jsverse/transloco";
import { ModuleType } from "@enums/ModuleType";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { FilterConfig } from "@models/workspace/FilterConfig";
import { FilterType } from "@enums/FilterType";
import { RendererConfig } from "@models/workspace/RendererConfig";
import { RendererType } from "@enums/RendererType";
import { FormatType } from "@enums/FormatType";
import { TabService } from "@services/tabs/TabService";
import { Pages } from "@constants/pages/Pages";
import { SearchKeyword } from "@models/workspace/SearchKeyword";
import { MoiDataService } from "../services/moi-data.service";
import { ToastrService, ActiveToast } from "ngx-toastr";
import { isGuid } from "@methods/uniqueMethods";
import { PermissionsService } from "@services/permissions/PermissionsService";
import { WorkspaceModulePermissionModel } from "@models/permissions/WorkspaceModulePermissions";
import { MoiTypes } from "@enums/moi/MoiTypes";
import { IncidentService } from "@app/incident/services/incident.service";
import { MoiOverviewModel } from "@models/moi/MoiOverviewModel";
import { nameof } from "@methods/jeffs-toolkit";
import { SettingsDataService } from "@services/http/SettingsDataService";
import { ApplicationSettings } from "@services/http/settings/application-settings";
import { KeyValue, KeyValuePair } from "@models/generic/KeyValuePair";

@Component({
    selector: "app-moi-overview",
    templateUrl: "./moi-overview.component.html",
    styleUrls: ["./moi-overview.component.scss"],
    standalone: false,
    providers: [provideTranslocoScope('moi')]
})
export class MoiOverviewComponent implements OnInit {
    private tabService = inject(TabService);
    private moiDataService = inject(MoiDataService);
    private toastr = inject(ToastrService);
    private pages = inject(Pages);
    private _permissions = inject(PermissionsService);
    private _incidentService = inject(IncidentService);
    private settingsDS = inject(SettingsDataService);
    private _transloco = inject(TranslocoService);
    private destroyRef = inject(DestroyRef);

    moduleType = ModuleType.BIM;
    tabID: string;
    tab: TabModel;
    data: Promise<MoiOverviewModel[]>;
    headerLookup: { [propName: string]: string } = {};
    useIncidents: boolean = false;
    filterConfigs: FilterConfig[] = [];

    rendererConfig: RendererConfig[] = [
        {
            textColumn: nameof<MoiOverviewModel>((x) => x.dueDate),
            actionColumn: nameof<MoiOverviewModel>((x) => x.isOverdue),
            actionValue: "Yes",
            type: RendererType.DueDate,
            formatType: FormatType.DateFormat,
        },
        {
            textColumn: nameof<MoiOverviewModel>((x) => x.progress),
            type: RendererType.Progress,
        },
        {
            textColumn: nameof<MoiOverviewModel>((x) => x.implementationScore),
            actionColumn: nameof<MoiOverviewModel>((x) => x.implementationScoreColor),
            hideActionColumn: true,
            type: RendererType.Score,
        },
        {
            textColumn: nameof<MoiOverviewModel>((x) => x.overallScore),
            actionColumn: nameof<MoiOverviewModel>((x) => x.overallScoreColor),
            hideActionColumn: true,
            type: RendererType.Score,
        },
        {
            textColumn: nameof<MoiOverviewModel>((x) => x.assessmentScore),
            actionColumn: nameof<MoiOverviewModel>((x) => x.assessmentScoreColor),
            hideActionColumn: true,
            type: RendererType.Score,
        },
    ];

    numberProps: string[] = [nameof<MoiOverviewModel>((x) => x.progress)];
    dateProps: string[] = [
        nameof<MoiOverviewModel>((x) => x.dueDate),
        nameof<MoiOverviewModel>((x) => x.startDate),
        nameof<MoiOverviewModel>((x) => x.moiDateCreated),
        nameof<MoiOverviewModel>((x) => x.initialDueDate),
        nameof<MoiOverviewModel>((x) => x.commentDate),
        nameof<MoiOverviewModel>((x) => x.reportDate),
        nameof<MoiOverviewModel>((x) => x.reportDueDate),
        nameof<MoiOverviewModel>((x) => x.assessmentDate),
        nameof<MoiOverviewModel>((x) => x.dateStatusChanged),
    ];

    permissions: WorkspaceModulePermissionModel;

    currentlyLoadedPeriod = null;
    periodFilterToast: ActiveToast<any> = null;

    async ngOnInit() {
        this.useIncidents = (
            await firstValueFrom(this.settingsDS.getSetting(ApplicationSettings.UseIncidents))
        ).BoolValue;
        await firstValueFrom(this._transloco.load(`moi/${this._transloco.getActiveLang()}`));
        this.filterConfigs = this.buildFilterConfigs();
        this.headerLookup = this.buildHeaderLookup();
        this.setDateFilters();
        this.setPermissions();
        this.loadData(this.currentlyLoadedPeriod);

        this._transloco.langChanges$
            .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
            .subscribe(async (lang) => {
                await firstValueFrom(this._transloco.load(`moi/${lang}`));
                this.filterConfigs = this.buildFilterConfigs();
                // Merge so mapHeaders' fallback property-name entries survive the rebuild.
                this.headerLookup = { ...this.headerLookup, ...this.buildHeaderLookup() };
            });
    }

    private setDateFilters() {
        this.dateProps.forEach((x) => {
            this.rendererConfig.push(<RendererConfig>{
                textColumn: x,
                type: RendererType.Default,
                formatType: FormatType.DateFormat,
            });
        });
    }

    private buildFilterConfigs(): FilterConfig[] {
        return [
            {
                label: this._transloco.translate('moi.overview.filterOrganizationsResponsible'),
                column: nameof<MoiOverviewModel>((x) => x.organizationID),
                type: FilterType.Organization,
                hideActionColumn: true,
                param: "",
            },
            {
                label: this._transloco.translate('moi.overview.filterOrganizationsReviewer'),
                column: nameof<MoiOverviewModel>((x) => x.organizationReviewerID),
                type: FilterType.Organization,
                hideActionColumn: true,
                param: "",
            },
            {
                label: this._transloco.translate('moi.overview.filterBusinessDimensions'),
                column: nameof<MoiOverviewModel>((x) => x.businessDimensionIDs),
                type: FilterType.BusinessDimensions,
                hideActionColumn: true,
                param: "",
            },
            {
                label: this._transloco.translate('moi.overview.filterFrameworkDimensions'),
                column: nameof<MoiOverviewModel>((x) => x.frameworkDimensionIDs),
                type: FilterType.FrameworkDimensions,
                hideActionColumn: true,
                param: "",
            },
            {
                label: this._transloco.translate('moi.overview.filterPeriod'),
                column: "Period",
                type: FilterType.Period,
                param: "",
                remoteFilter: true,
                module: ModuleType.BIM,
            },
        ];
    }

    private buildHeaderLookup(): { [propName: string]: string } {
        const t = (key: string) => this._transloco.translate(`moi.overview.${key}`);
        const headerLookup: { [propName: string]: string } = {};
        headerLookup[nameof<MoiOverviewModel>((x) => x.moiIdentifier)] = t('headerIdentifier');
        headerLookup[nameof<MoiOverviewModel>((x) => x.moiName)] = t('headerName');
        headerLookup[nameof<MoiOverviewModel>((x) => x.guid)] = t('headerGuid');
        headerLookup[nameof<MoiOverviewModel>((x) => x.subject)] = t('headerSubject');
        headerLookup[nameof<MoiOverviewModel>((x) => x.status)] = t('headerStatus');
        headerLookup[nameof<MoiOverviewModel>((x) => x.priority)] = t('headerPriority');
        headerLookup[nameof<MoiOverviewModel>((x) => x.feasibility)] = t('headerFeasibility');
        headerLookup[nameof<MoiOverviewModel>((x) => x.progress)] = t('headerProgress');
        headerLookup[nameof<MoiOverviewModel>((x) => x.organizationResponsible)] = t('headerOrganizationResponsible');
        headerLookup[nameof<MoiOverviewModel>((x) => x.moiResponsible)] = t('headerResponsible');
        headerLookup[nameof<MoiOverviewModel>((x) => x.delegate)] = t('headerDelegate');
        headerLookup[nameof<MoiOverviewModel>((x) => x.organizationReviewer)] = t('headerOrganizationReviewer');
        headerLookup[nameof<MoiOverviewModel>((x) => x.reviewer)] = t('headerMoiReviewer');
        headerLookup[nameof<MoiOverviewModel>((x) => x.findingDescription)] = t('headerFindingDescription');
        headerLookup[nameof<MoiOverviewModel>((x) => x.startDate)] = t('headerStartDate');
        headerLookup[nameof<MoiOverviewModel>((x) => x.moiDateCreated)] = t('headerDateCreated');
        headerLookup[nameof<MoiOverviewModel>((x) => x.dueDate)] = t('headerDueDate');
        headerLookup[nameof<MoiOverviewModel>((x) => x.initialDueDate)] = t('headerInitialDueDate');
        headerLookup[nameof<MoiOverviewModel>((x) => x.implementationScore)] = t('headerImplementationScore');
        headerLookup[nameof<MoiOverviewModel>((x) => x.recommendation)] = t('headerRecommendation');
        headerLookup[nameof<MoiOverviewModel>((x) => x.managementResponse)] = t('headerManagementResponse');
        headerLookup[nameof<MoiOverviewModel>((x) => x.lastComment)] = t('headerLastComment');
        headerLookup[nameof<MoiOverviewModel>((x) => x.commentDate)] = t('headerCommentDate');
        headerLookup[nameof<MoiOverviewModel>((x) => x.businessDimensions)] = t('headerBusinessDimensions');
        headerLookup[nameof<MoiOverviewModel>((x) => x.frameworkDimensions)] = t('headerFrameworkDimensions');
        headerLookup[nameof<MoiOverviewModel>((x) => x.isOverdue)] = t('headerIsOverdue');
        headerLookup[nameof<MoiOverviewModel>((x) => x.dateStatusChanged)] = t('headerDateStatusChanged');
        headerLookup[nameof<MoiOverviewModel>((x) => x.overallScore)] = t('headerOverallScore');
        headerLookup[nameof<MoiOverviewModel>((x) => x.overallScoreColor)] = t('headerOverallScoreColor');
        headerLookup[nameof<MoiOverviewModel>((x) => x.organizationID)] = t('headerOrganizationResponsibleId');
        headerLookup[nameof<MoiOverviewModel>((x) => x.organizationReviewerID)] = t('headerOrganizationReviewerId');
        headerLookup[nameof<MoiOverviewModel>((x) => x.moiType)] = t('headerMoiType');
        headerLookup[nameof<MoiOverviewModel>((x) => x.implementationScoreColor)] = t('headerImplementationScoreColor');
        headerLookup[nameof<MoiOverviewModel>((x) => x.businessDimensionIDs)] = t('headerBusinessDimensionIds');
        headerLookup[nameof<MoiOverviewModel>((x) => x.frameworkDimensionIDs)] = t('headerFrameworkDimensionIds');
        headerLookup[nameof<MoiOverviewModel>((x) => x.internalAuditComment)] = t('headerLastInternalAuditComment');
        headerLookup[nameof<MoiOverviewModel>((x) => x.reportNo)] = t('headerReportNo');
        headerLookup[nameof<MoiOverviewModel>((x) => x.reportType)] = t('headerReportType');
        headerLookup[nameof<MoiOverviewModel>((x) => x.objectName)] = t('headerReportName');
        headerLookup[nameof<MoiOverviewModel>((x) => x.reportDate)] = t('headerReportDate');
        headerLookup[nameof<MoiOverviewModel>((x) => x.reportDueDate)] = t('headerReportDueDate');
        headerLookup[nameof<MoiOverviewModel>((x) => x.conclusion)] = t('headerConclusion');
        headerLookup[nameof<MoiOverviewModel>((x) => x.assessmentType)] = t('headerAssessmentType');
        headerLookup[nameof<MoiOverviewModel>((x) => x.assessmentDate)] = t('headerAssessmentDate');
        headerLookup[nameof<MoiOverviewModel>((x) => x.assessmentScore)] = t('headerAssessmentScore');
        headerLookup[nameof<MoiOverviewModel>((x) => x.assessmentScoreColor)] = t('headerAssessmentScoreColor');
        headerLookup[nameof<MoiOverviewModel>((x) => x.parentIdentifier)] = t('headerLinkedIdentifier');
        headerLookup[nameof<MoiOverviewModel>((x) => x.parentName)] = t('headerLinkedName');
        headerLookup[nameof<MoiOverviewModel>((x) => x.parentDescription)] = t('headerLinkedDescription');
        headerLookup[nameof<MoiOverviewModel>((x) => x.organizationPath)] = t('headerOrganizationPath');
        headerLookup[nameof<MoiOverviewModel>((x) => x.findingReportID)] = t('headerFindingReportId');
        headerLookup[nameof<MoiOverviewModel>((x) => x.url)] = t('headerMoiUrl');
        return headerLookup;
    }

    async openMoi(moi: any) {
        if (this.currentlyLoadedPeriod > 0) {
            // Check whether the user has access to the moi, store the result in the moi object to avoid another call
            if (moi.hasAccess == null) {
                moi.hasAccess = await firstValueFrom(this.moiDataService.hasAccessToMoi(moi.guid));
            }

            if (!moi.hasAccess) {
                this.createPeriodToast(
                    this._transloco.translate('moi.overview.unableToOpenMoi'),
                    this._transloco.translate('moi.overview.moiDeletedInCurrentPeriod')
                );

                return;
            }

            this.createPeriodToast(
                this._transloco.translate('moi.overview.periodFilterApplied'),
                this._transloco.translate('moi.overview.openingCurrentStateMoi'),
                true
            );

            this.openTab(moi.guid, this._transloco.translate('moi.overview.measureOfImprovementTabTitle'));

            return;
        }

        this.openTab(moi.guid, this._transloco.translate('moi.overview.measureOfImprovementTabTitle'));
    }

    createPeriodToast(title: string, msg: string, persistent?: boolean) {
        if (this.periodFilterToast) {
            this.periodFilterToast.toastRef.close();
        }

        this.periodFilterToast = this.toastr.warning(msg, title, { disableTimeOut: persistent });
    }

    addMoi() {
        this.openTab(null, this._transloco.translate('moi.overview.newMeasureOfImprovementTabTitle'));
    }

    private openTab(guid: string, name: string) {
        this.tabService.generateTab(this.pages.MoiDetail, guid, name);
    }

    getData(keyWords: SearchKeyword[]) {
        this.currentlyLoadedPeriod = null;
        for (const keyWord of keyWords) {
            const columnName = keyWord.col.toLowerCase();
            switch (columnName) {
                case "period": {
                    const periodID = parseInt(keyWord.searchValue, 0);
                    if (periodID > 0) {
                        this.currentlyLoadedPeriod = periodID;
                    }
                    break;
                }
            }
        }

        this.loadData(this.currentlyLoadedPeriod);
    }

    private setPermissions(): void {
        this.permissions = this._permissions.permissions.Workspace.bim;
    }

    private loadData(periodId: number = null): void {
        this.data = firstValueFrom(this.moiDataService
            .getMois(periodId))
            .then((mois) => {
                this.getParentDataFromIncidents(mois);
                this.mapHeaders(mois);

                return mois;
            });
    }

    private async getParentDataFromIncidents(data: MoiOverviewModel[]): Promise<void> {
        if (this.useIncidents == false) {
            return;
        }

        const guidMoiIdPair: KeyValue<string, string>[] = await firstValueFrom(
            this.moiDataService.getIncidentMoiParentGuids()
        );

        const incidentIds = Object.keys(guidMoiIdPair).map((x) => guidMoiIdPair[x]);

        this._incidentService.getIncidentsByGuids(incidentIds).subscribe((incidents) => {
            Object.keys(guidMoiIdPair).forEach((key) => {
                const moi = data.find((x) => x.guid == key);
                const incident = incidents.filter((x) => x.id == guidMoiIdPair[key]).first();
                if (moi && incident) {
                    moi.parentIdentifier = "(I) " + incident.identifier;
                    moi.parentName = "(I) " + incident.name;
                    moi.parentDescription = "(I) " + incident.description;
                }
            });
        });
    }

    private mapHeaders = (rows) => {
        if (rows && rows.length) {
            Object.getOwnPropertyNames(rows[0]).forEach((propName) => {
                if (!this.headerLookup[propName]) {
                    this.headerLookup[propName] = propName;
                }
            });
        }
        return rows;
    };
}
